import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const SESSIONS = [
  { icon: '☁️', title: 'AWS Cloud Training', desc: 'EC2, S3, Lambda, VPC, RDS, CloudFormation, IAM. Full certification prep paths for CCP, SAA, DVA.', badges: ['⏱ 60–90 min', '📜 Cert Ready'], topic: 'AWS+Cloud' },
  { icon: '🔷', title: '.NET / C# Training', desc: 'ASP.NET Core, EF Core, REST APIs, CQRS, microservices, gRPC, Blazor, Azure cloud integration.', badges: ['⏱ 60–90 min', '🏢 Enterprise'], topic: '.NET+C%23' },
  { icon: '☕', title: 'Java Development', desc: 'Core Java, Spring Boot, Hibernate, REST APIs, concurrency, JVM tuning, production microservices.', badges: ['⏱ 60–90 min', '🏢 Enterprise'], topic: 'Java' },
  { icon: '🐍', title: 'Python Training', desc: 'Scripting, OOP, async, FastAPI, Django, data pipelines, cloud automation with boto3 and Ansible.', badges: ['⏱ 60–90 min', '🔬 DevOps Ready'], topic: 'Python' },
  { icon: '⚡', title: 'C & C++ Training', desc: 'Pointers, memory management, STL, system programming, competitive programming, embedded systems.', badges: ['⏱ 60–90 min', '⚡ Performance'], topic: 'C+C%2B%2B' },
  { icon: '🧩', title: 'Data Structures & Algorithms', desc: 'Arrays, trees, graphs, DP, backtracking — tailored problem-solving sessions for FAANG interviews.', badges: ['⏱ 60–90 min', '🎯 Interview Prep'], topic: 'Data+Structures' },
  { icon: '⚙️', title: 'System Design', desc: 'Scalable architecture, CAP theorem, messaging, caching, database design for senior engineer interviews.', badges: ['⏱ 90 min', '🏗️ Architecture'], topic: 'System+Design' },
  { icon: '🏗️', title: 'Cloud Architecture', desc: 'Well-Architected Framework, multi-region design, disaster recovery, cost optimization, IaC.', badges: ['⏱ 90 min', '🏆 Expert Level'], topic: 'Cloud+Architecture' },
  { icon: '🤖', title: 'AI & Machine Learning', desc: 'Supervised & unsupervised ML, neural networks, model training & evaluation, scikit-learn, TensorFlow, PyTorch.', badges: ['⏱ 60–90 min', '🧠 ML Ready'], topic: 'AI+Machine+Learning', iconBg: 'var(--orange-pale)' },
  { icon: '✨', title: 'Generative AI & LLMs', desc: 'Prompt engineering, RAG pipelines, fine-tuning, LangChain, vector databases, building AI-powered applications.', badges: ['⏱ 60–90 min', '🚀 Cutting Edge'], topic: 'Generative+AI', iconBg: 'var(--orange-pale)' },
];

const TrainingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="page-hero" style={{ background: 'linear-gradient(135deg,var(--orange-pale),var(--blue-pale))' }}>
        <span className="section-tag orange">1-on-1 Training</span>
        <h1>Personalized <span className="text-orange">Expert Mentorship</span></h1>
        <p>Live one-on-one sessions with certified instructors — completely tailored to your learning pace and career goals.</p>
      </div>

      {/* HOW IT WORKS */}
      <div className="section" style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="section-center" style={{ marginBottom: '36px' }}>
          <span className="section-tag">How It Works</span>
          <h2 className="section-h2" style={{ marginTop: '8px' }}>Simple 3-Step Process</h2>
        </div>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '20px', maxWidth: '760px' }}>
          {[
            { n: '1', color: 'var(--blue-pale)', text: 'var(--blue)', title: 'Choose Your Topic', desc: 'Select from AWS, Python, Java, .NET, C++, DSA, or System Design' },
            { n: '2', color: 'var(--orange-pale)', text: 'var(--orange)', title: 'Submit Request', desc: 'Fill in your details and preferred timing. We confirm within 4 hours.' },
            { n: '3', color: 'var(--green-pale)', text: 'var(--green)', title: 'Start Learning', desc: 'Attend live 1-on-1 sessions, ask questions, and get personalized guidance.' },
          ].map((step, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '24px 16px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: step.color, color: step.text, fontFamily: "'Sora',sans-serif", fontSize: '1.3rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>{step.n}</div>
              <h3 style={{ fontSize: '0.97rem', fontWeight: 700, marginBottom: '6px' }}>{step.title}</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* TRAINING OPTIONS */}
      <div className="section">
        <div className="section-center" style={{ marginBottom: '36px' }}>
          <span className="section-tag">Available Sessions</span>
          <h2 className="section-h2" style={{ marginTop: '8px' }}>Choose Your Learning Path</h2>
        </div>
        <div className="cards-grid container">
          {SESSIONS.map((s, i) => (
            <div key={i} className="training-card" onClick={() => navigate(`/request?topic=${s.topic}`)}>
              <div className="tc-icon" style={s.iconBg ? { background: s.iconBg } : {}}>{s.icon}</div>
              <h3>{s.title}</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>{s.desc}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {s.badges.map((b, j) => <span key={j} className="tc-badge">{b}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SESSION TYPES */}
      <div style={{ background: 'var(--blue-pale)', padding: '56px 6%', borderTop: '1px solid var(--blue-soft)', borderBottom: '1px solid var(--blue-soft)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div className="section-center" style={{ marginBottom: '32px' }}>
            <span className="section-tag">Session Types</span>
            <h2 className="section-h2" style={{ marginTop: '8px' }}>Choose Your Commitment</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '14px' }}>
            {[
              { icon: '⚡', title: 'Single Session', desc: '1-hour deep dive on a specific topic', border: '1.5px solid var(--gray-200)' },
              { icon: '📅', title: 'Weekly (1 Month)', desc: '4 sessions over a month with progress tracking', border: '2px solid var(--blue)', popular: true },
              { icon: '🔥', title: 'Bootcamp (1 Week)', desc: 'Intensive 5-day immersive learning sprint', border: '1.5px solid var(--gray-200)' },
              { icon: '✨', title: 'Custom Plan', desc: 'Fully personalized schedule and curriculum', border: '1.5px solid var(--gray-200)' },
            ].map((t, i) => (
              <div key={i} style={{ background: '#fff', border: t.border, borderRadius: 'var(--radius)', padding: '20px', textAlign: 'center', position: 'relative' }}>
                {t.popular && <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: 'var(--orange)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: '50px' }}>POPULAR</div>}
                <div style={{ fontSize: '1.6rem', marginBottom: '9px' }}>{t.icon}</div>
                <h3 style={{ fontSize: '0.93rem', fontWeight: 800, marginBottom: '6px' }}>{t.title}</h3>
                <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{t.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '28px' }}>
            <Link to="/request?tab=session" className="btn btn-orange btn-lg">Book a Session Now</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TrainingPage;
